import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    CheckCircle2Icon,
    ExternalLinkIcon,
    HandIcon,
    PlayCircleIcon,
    RocketIcon,
} from 'lucide-react';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress, ProgressIndicator, ProgressTrack } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { useClub } from '../ClubContext';
import { getClubTutorialLessons, parseYoutubeId } from '../constants/clubTutorialLessons';
import { useClubTutorialProgress } from '../hooks/useClubTutorialProgress';

function YoutubeEmbed({ youtubeId }) {
    const videoId = parseYoutubeId(youtubeId);

    if (!videoId) {
        return (
            <div className="flex aspect-video flex-col items-center justify-center gap-2 rounded-xl border border-dashed bg-muted/50">
                <PlayCircleIcon className="size-12 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">Vídeo em breve</p>
            </div>
        );
    }

    return (
        <div className="relative aspect-video overflow-hidden rounded-xl bg-black">
            <iframe
                title="Vídeo tutorial"
                src={`https://www.youtube.com/embed/${videoId}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 size-full border-0"
            />
        </div>
    );
}

function LessonVideoSection({ youtubeId }) {
    const [open, setOpen] = useState(false);
    const hasVideo = Boolean(parseYoutubeId(youtubeId));

    return (
        <Accordion
            value={open ? ['video'] : []}
            onValueChange={values => setOpen(values.includes('video'))}
            className="rounded-lg border border-border"
        >
            <AccordionItem value="video" className="border-0">
                <AccordionTrigger className="px-3 py-3 hover:no-underline">
                    <span className="flex items-center gap-2 text-sm font-medium">
                        <PlayCircleIcon className="size-4 text-primary" />
                        {hasVideo ? 'Assistir vídeo da lição' : 'Vídeo da lição'}
                    </span>
                </AccordionTrigger>
                <AccordionContent className="px-3 pb-3">
                    {open ? <YoutubeEmbed youtubeId={youtubeId} /> : null}
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
}

function LessonAccordionItem({ lesson, step, completed, onComplete }) {
    return (
        <AccordionItem
            value={lesson.id}
            className={cn(
                'overflow-hidden rounded-xl border border-border bg-card shadow-sm not-last:border-b',
                completed && 'border-emerald-500/40 bg-emerald-50/40',
            )}
        >
            <AccordionTrigger className="px-4 py-4 hover:no-underline">
                <div className="flex flex-1 items-start justify-between gap-3 pr-2">
                    <div className="flex flex-col gap-1 text-left">
                        <span className="text-base font-semibold">
                            <span className="mr-2 text-sm font-medium text-muted-foreground">{step}.</span>
                            {lesson.title}
                        </span>
                        <span className="text-sm font-normal text-muted-foreground">{lesson.description}</span>
                    </div>
                    {completed ? (
                        <Badge
                            variant="secondary"
                            className="shrink-0 border-emerald-200 bg-emerald-100 text-emerald-800"
                        >
                            <CheckCircle2Icon data-icon="inline-start" />
                            Concluída
                        </Badge>
                    ) : null}
                </div>
            </AccordionTrigger>
            <AccordionContent className="flex flex-col gap-4 px-4 pb-4">
                <LessonVideoSection youtubeId={lesson.youtubeId} />
                <div className="flex flex-col gap-2 sm:flex-row">
                    <Button
                        variant={completed ? 'outline' : 'default'}
                        disabled={completed}
                        className="flex-1"
                        onClick={() => onComplete(lesson.id)}
                    >
                        <CheckCircle2Icon data-icon="inline-start" />
                        {completed ? 'Lição concluída' : 'Concluí lição'}
                    </Button>
                    {lesson.path ? (
                        <Button variant="outline" className="flex-1" render={<Link to={lesson.path} />}>
                            Ir para a tela
                            <ExternalLinkIcon data-icon="inline-end" />
                        </Button>
                    ) : null}
                </div>
            </AccordionContent>
        </AccordionItem>
    );
}

export default function ClubTutorial() {
    const { hasResale, isReseller, hasBenefits, canViewFinance } = useClub();

    const lessons = useMemo(
        () => getClubTutorialLessons({ hasResale, hasPlans: isReseller || hasBenefits, canViewFinance }),
        [hasResale, isReseller, hasBenefits, canViewFinance],
    );
    const lessonIds = useMemo(() => lessons.map(l => l.id), [lessons]);

    const { completeStep, isCompleted, progress, completedCount, total, allCompleted } =
        useClubTutorialProgress(lessonIds);

    const defaultOpen = useMemo(() => {
        const pending = lessons.find(lesson => !isCompleted(lesson.id));
        return pending ? [pending.id] : lessons[0] ? [lessons[0].id] : [];
    }, [lessons, isCompleted]);

    return (
        <div className="flex flex-col gap-6">
            <Card className="overflow-hidden border-0 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-md">
                <CardContent className="flex flex-col gap-4 p-6 md:p-8">
                    <div className="flex items-center gap-3">
                        <HandIcon className="size-9" />
                        <h1 className="text-2xl font-bold tracking-tight md:text-3xl">Tutorial de configuração</h1>
                    </div>
                    <p className="max-w-2xl text-sm opacity-90 md:text-base">
                        Assista às lições abaixo e marque cada uma como concluída. Quando terminar todas, seu clube
                        estará pronto para operar.
                    </p>
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between text-sm">
                            <span className="opacity-90">
                                Progresso: {completedCount}/{total} lições
                            </span>
                            <Badge variant="secondary" className="border-0 bg-white/20 text-inherit">
                                {progress}%
                            </Badge>
                        </div>
                        <Progress value={progress} className="w-full">
                            <ProgressTrack className="h-2 bg-white/25">
                                <ProgressIndicator className="bg-white" />
                            </ProgressTrack>
                        </Progress>
                    </div>
                    {allCompleted ? (
                        <div className="flex items-center gap-2 text-sm font-semibold">
                            <RocketIcon className="size-4" />
                            Parabéns! Você concluiu todas as lições do tutorial.
                        </div>
                    ) : null}
                </CardContent>
            </Card>

            <Accordion multiple defaultValue={defaultOpen} className="flex flex-col gap-3">
                {lessons.map((lesson, index) => (
                    <LessonAccordionItem
                        key={lesson.id}
                        lesson={lesson}
                        step={index + 1}
                        completed={isCompleted(lesson.id)}
                        onComplete={completeStep}
                    />
                ))}
            </Accordion>
        </div>
    );
}
